import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, Divider, Table, TableHead, TableRow,
  TableCell, LinearProgress, Badge,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import KeyIcon from '@mui/icons-material/Key';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import {
  MY_CEREMONIES_QUERY,
  MY_GUESTS_QUERY,
  MY_VENUE_BOOKINGS_QUERY,
  MY_PARKING_SLOTS_QUERY,
  CREATE_PARKING_SLOT_MUTATION,
  UPDATE_PARKING_SLOT_MUTATION,
  DELETE_PARKING_SLOT_MUTATION,
  ALLOCATE_PARKING_MUTATION,
  RELEASE_PARKING_MUTATION,
} from '../graphql/queries';

const STATUS_COLOR = { available: 'success', reserved: 'warning', occupied: 'error' };
const SLOT_TYPE_ICON = { car: <DirectionsCarIcon fontSize="small" />, bike: <TwoWheelerIcon fontSize="small" />, van: <DirectionsCarIcon fontSize="small" />, valet: <KeyIcon fontSize="small" />, handicap: <LocalParkingIcon fontSize="small" /> };
const EMPTY_SLOT = { ceremonyId: '', zoneName: 'General', slotNumber: '', slotType: 'car', notes: '' };
const EMPTY_ALLOC = { guestId: '', guestName: '', vehicleNumber: '', vehicleType: 'car', isValet: false, notes: '' };

export default function ParkingPage() {
  const navigate = useNavigate();
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [slotDialog, setSlotDialog] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [slotForm, setSlotForm] = useState(EMPTY_SLOT);
  const [allocDialog, setAllocDialog] = useState(false);
  const [allocSlot, setAllocSlot] = useState(null);
  const [allocForm, setAllocForm] = useState(EMPTY_ALLOC);
  const [error, setError] = useState('');

  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: guestData }    = useQuery(MY_GUESTS_QUERY,    { fetchPolicy: 'cache-and-network' });
  const { data: bookingData, loading: bookingsLoading } = useQuery(MY_VENUE_BOOKINGS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data, loading, refetch } = useQuery(MY_PARKING_SLOTS_QUERY, {
    variables: { venueId: selectedVenueId },
    skip: !selectedVenueId,
    fetchPolicy: 'cache-and-network',
  });

  const ceremonies = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];
  const guests     = guestData?.myGuests || [];
  const slots      = selectedVenueId ? (data?.myParkingSlots || []) : [];

  // Parking is managed per venue you've actually booked — dedupe active bookings by venue.
  const activeBookings = (bookingData?.myVenueBookings || []).filter(b => b.status !== 'cancelled');
  const bookedVenues = Array.from(
    activeBookings.reduce((map, b) => (b.venue && !map.has(b.venue.id) ? map.set(b.venue.id, b.venue) : map), new Map()).values()
  );
  const selectedVenue = bookedVenues.find(v => v.id === selectedVenueId) || null;
  const selectedBooking = activeBookings.find(b => b.venue?.id === selectedVenueId) || null;

  const stats = selectedVenueId ? {
    total:     slots.length,
    available: slots.filter(s => s.status === 'available').length,
    reserved:  slots.filter(s => s.status === 'reserved').length,
    occupied:  slots.filter(s => s.status === 'occupied').length,
    valetCount: slots.reduce((n, s) => n + (s.allocations || []).filter(a => a.isValet && !a.releasedAt).length, 0),
  } : null;

  const [createSlot] = useMutation(CREATE_PARKING_SLOT_MUTATION, {
    onCompleted: () => { setSlotDialog(false); setSlotForm(EMPTY_SLOT); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateSlot] = useMutation(UPDATE_PARKING_SLOT_MUTATION, {
    onCompleted: () => { setSlotDialog(false); setEditSlot(null); setSlotForm(EMPTY_SLOT); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteSlot] = useMutation(DELETE_PARKING_SLOT_MUTATION, { onCompleted: () => refetch() });
  const [allocate]   = useMutation(ALLOCATE_PARKING_MUTATION,   {
    onCompleted: () => { setAllocDialog(false); setAllocSlot(null); setAllocForm(EMPTY_ALLOC); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [releaseParking] = useMutation(RELEASE_PARKING_MUTATION, { onCompleted: () => refetch() });

  const openCreateSlot = () => { setEditSlot(null); setSlotForm({ ...EMPTY_SLOT, ceremonyId: selectedBooking?.ceremony?.id || '' }); setSlotDialog(true); };
  const openEditSlot   = (s) => { setEditSlot(s); setSlotForm({ ceremonyId: s.ceremonyId || '', zoneName: s.zoneName, slotNumber: s.slotNumber, slotType: s.slotType, notes: s.notes || '' }); setSlotDialog(true); };
  const openAlloc      = (s) => { setAllocSlot(s); setAllocForm(EMPTY_ALLOC); setAllocDialog(true); };

  const handleSaveSlot = () => {
    const vars = { ...slotForm, ceremonyId: slotForm.ceremonyId || null };
    if (editSlot) updateSlot({ variables: { id: editSlot.id, ...vars } });
    else createSlot({ variables: { ...vars, venueId: selectedVenueId } });
  };
  const handleAllocate = () => {
    allocate({ variables: { slotId: allocSlot.id, ...allocForm, guestId: allocForm.guestId || null } });
  };

  const grouped = slots.reduce((acc, s) => {
    const zone = s.zoneName || 'General';
    if (!acc[zone]) acc[zone] = [];
    acc[zone].push(s);
    return acc;
  }, {});

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={1} alignItems="center">
          <LocalParkingIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>Parking & Valet</Typography>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateSlot} disabled={!selectedVenueId}>Add Slot</Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Parking is managed per venue — select a venue you've booked to add or view its parking details.
      </Typography>

      {bookingsLoading && <LinearProgress sx={{ mb: 2 }} />}

      {!bookingsLoading && bookedVenues.length === 0 && (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <HolidayVillageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            You haven't booked any venues yet. Book a venue first to manage its parking.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/dashboard/venues')}>Browse Venues</Button>
        </Card>
      )}

      {bookedVenues.length > 0 && (
        <Stack direction="row" spacing={1} mb={3} flexWrap="wrap" alignItems="center">
          {bookedVenues.map(v => (
            <Chip
              key={v.id}
              icon={<HolidayVillageIcon />}
              label={v.name}
              onClick={() => setSelectedVenueId(v.id === selectedVenueId ? '' : v.id)}
              color={selectedVenueId === v.id ? 'primary' : 'default'}
              variant={selectedVenueId === v.id ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
      )}

      {bookedVenues.length > 0 && !selectedVenueId && (
        <Alert severity="info" sx={{ mb: 3 }}>Select a venue above to manage its parking.</Alert>
      )}

      {/* Stats bar */}
      {selectedVenueId && stats && (
        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Total Slots', value: stats.total, color: 'default' },
            { label: 'Available',   value: stats.available, color: 'success' },
            { label: 'Reserved',    value: stats.reserved,  color: 'warning' },
            { label: 'Occupied',    value: stats.occupied,  color: 'error' },
            { label: 'Valet',       value: stats.valetCount, color: 'info' },
          ].map(s => (
            <Grid item xs={6} sm={4} md={2.4} key={s.label}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="h4" fontWeight={700} color={s.color === 'default' ? 'text.primary' : `${s.color}.main`}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedVenueId && loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {selectedVenueId && slots.length === 0 && !loading && (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <LocalParkingIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No parking slots yet for {selectedVenue?.name}. Click <strong>Add Slot</strong> to start.</Typography>
        </Card>
      )}

      {Object.entries(grouped).map(([zone, zoneSlots]) => (
        <Box key={zone} mb={3}>
          <Typography variant="h6" fontWeight={600} mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalParkingIcon fontSize="small" /> Zone: {zone}
            <Chip label={`${zoneSlots.length} slots`} size="small" />
          </Typography>
          <Table size="small" sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Slot #</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ceremony</TableCell>
                <TableCell>Current Allocation</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {zoneSlots.map(slot => {
                const activeAlloc = (slot.allocations || []).find(a => !a.releasedAt);
                const ceremony = ceremonies.find(c => c.id === slot.ceremonyId);
                return (
                  <TableRow key={slot.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {SLOT_TYPE_ICON[slot.slotType] || <LocalParkingIcon fontSize="small" />}
                        <Typography fontWeight={600}>{slot.slotNumber}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{slot.slotType}</TableCell>
                    <TableCell><Chip label={slot.status} color={STATUS_COLOR[slot.status] || 'default'} size="small" /></TableCell>
                    <TableCell>{ceremony?.name || '—'}</TableCell>
                    <TableCell>
                      {activeAlloc ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{activeAlloc.guestName || activeAlloc.vehicleNumber}</Typography>
                          {activeAlloc.isValet && <Chip label="Valet" size="small" color="info" />}
                          <Tooltip title="Release slot">
                            <IconButton size="small" color="warning" onClick={() => releaseParking({ variables: { id: activeAlloc.id } })}>
                              <KeyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {slot.status === 'available' && (
                          <Tooltip title="Allocate slot">
                            <IconButton size="small" color="primary" onClick={() => openAlloc(slot)}><DirectionsCarIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditSlot(slot)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteSlot({ variables: { id: slot.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ))}

      {/* Add/Edit Slot Dialog */}
      <Dialog open={slotDialog} onClose={() => setSlotDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editSlot ? 'Edit Parking Slot' : `Add Parking Slot — ${selectedVenue?.name || ''}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="Ceremony" fullWidth value={slotForm.ceremonyId} onChange={e => setSlotForm(f => ({ ...f, ceremonyId: e.target.value }))}>
              <MenuItem value="">No specific ceremony</MenuItem>
              {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Zone Name" fullWidth value={slotForm.zoneName} onChange={e => setSlotForm(f => ({ ...f, zoneName: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Slot Number" fullWidth required value={slotForm.slotNumber} onChange={e => setSlotForm(f => ({ ...f, slotNumber: e.target.value }))} />
              </Grid>
            </Grid>
            <TextField select label="Slot Type" fullWidth value={slotForm.slotType} onChange={e => setSlotForm(f => ({ ...f, slotType: e.target.value }))}>
              {['car','bike','van','handicap','valet'].map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
            </TextField>
            <TextField label="Notes" fullWidth multiline rows={2} value={slotForm.notes} onChange={e => setSlotForm(f => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSlotDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSlot} disabled={!slotForm.slotNumber}>{editSlot ? 'Save' : 'Add Slot'}</Button>
        </DialogActions>
      </Dialog>

      {/* Allocate Slot Dialog */}
      <Dialog open={allocDialog} onClose={() => setAllocDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Allocate Slot {allocSlot?.slotNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="Guest (optional)" fullWidth value={allocForm.guestId} onChange={e => { const g = guests.find(x => x.id === e.target.value); setAllocForm(f => ({ ...f, guestId: e.target.value, guestName: g?.name || f.guestName })); }}>
              <MenuItem value="">Select a guest</MenuItem>
              {guests.map(g => <MenuItem key={g.id} value={g.id}>{g.name}{g.isVip ? ' (VIP)' : ''}</MenuItem>)}
            </TextField>
            <TextField label="Guest Name (if no guest selected)" fullWidth value={allocForm.guestName} onChange={e => setAllocForm(f => ({ ...f, guestName: e.target.value }))} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Vehicle Number" fullWidth value={allocForm.vehicleNumber} onChange={e => setAllocForm(f => ({ ...f, vehicleNumber: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField select label="Vehicle Type" fullWidth value={allocForm.vehicleType} onChange={e => setAllocForm(f => ({ ...f, vehicleType: e.target.value }))}>
                  {['car','bike','van','suv','auto'].map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <TextField select label="Valet Service?" fullWidth value={allocForm.isValet ? 'yes' : 'no'} onChange={e => setAllocForm(f => ({ ...f, isValet: e.target.value === 'yes' }))}>
              <MenuItem value="no">No — self park</MenuItem>
              <MenuItem value="yes">Yes — valet managed</MenuItem>
            </TextField>
            <TextField label="Notes" fullWidth multiline rows={2} value={allocForm.notes} onChange={e => setAllocForm(f => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllocDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAllocate}>Allocate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
